class PregnancyQuotas
{
  constructor ()
  {
    // donorTitle|TREATMENT_COUNTRY -> boolean
    this.cache = new Map();
    // donorTitle|TREATMENT_COUNTRY -> Promise<boolean> (in-flight dedupe)
    this.inflight = new Map();
  }

  // Donors capped at this many worldwide families (or fewer) sell the
  // flat-priced "max-5" quota variant instead of a country bucket.
  // (Getter, not a static class field — field syntax is a parse error on
  // Safari < 14.1; see TreatmentCountry.PENDING_SYNC_SECONDS.)
  static get MAX_FIVE_FAMILY_LIMIT ()
  {
    return 5;
  }

  hasQuota (donorTitle, treatmentCountry)
  {
    if (!window.customBackend.customerId) {
      return Promise.resolve(false);
    }
    if (!donorTitle || !treatmentCountry) {
      return Promise.resolve(false);
    }

    const cacheKey = donorTitle + '|' + treatmentCountry.toUpperCase();

    if (this.cache.has(cacheKey)) {
      return Promise.resolve(this.cache.get(cacheKey));
    }

    if (this.inflight.has(cacheKey)) {
      return this.inflight.get(cacheKey);
    }

    const url = app.apiUrl('customer/check-quota')
      + '?donor=' + encodeURIComponent(donorTitle)
      + '&treatment_country=' + encodeURIComponent(treatmentCountry);

    const promise = fetch(url, { headers: app.apiHeaders() })
      .then(res => {
        if (!res.ok) {
          throw new Error('check-quota returned ' + res.status);
        }
        return res.json();
      })
      .then(data => {
        const result = !!(data && data.data && data.data.has_quota);
        this.cache.set(cacheKey, result);
        this.inflight.delete(cacheKey);
        return result;
      })
      .catch(err => {
        this.inflight.delete(cacheKey);
        console.error('[PregnancyQuotas] check-quota failed for donor', donorTitle, treatmentCountry, ':', err);
        throw err;
      });

    this.inflight.set(cacheKey, promise);
    return promise;
  }

  getPregnancyQuotaVariant (treatmentCountry, residentialCountry, donorFamilyLimit)
  {
    // A scarce donor (worldwide family cap at MAX_FIVE_FAMILY_LIMIT or below)
    // always sells the flat-priced max-5 variant — it outranks every pricing
    // bucket, including the backend-resolved one remembered below.
    const maxFiveVariant = this.maxFiveQuotaVariant(donorFamilyLimit);

    if (maxFiveVariant) {
      return maxFiveVariant;
    }

    if (!treatmentCountry) {
      const resolvedCountry = app.treatmentCountry.getSelection().country;

      treatmentCountry = resolvedCountry
        ? resolvedCountry.toLowerCase()
        : '';
    }

    if (!residentialCountry) {
      residentialCountry = app.customer.residentialCountry
        ? app.customer.residentialCountry.toLowerCase()
        : '';
    }

    const globalPregnancyQuotaVariant = window.theme.pregnancyQuotaVariants.find(quota => quota.country === 'global');

    // Prefer the backend's resolved pricing bucket when we have it (captured from
    // a /quota-available response). This prices cross-border counts-regardless /
    // residence-dominant quotas against the country the backend actually counts
    // them in — the residence===treatment heuristic below gets those wrong. Falls
    // back to the heuristic when the backend hasn't supplied a bucket (older
    // backend, or no quota check made yet), so this is safe to ship ahead of BE.
    const backendBucket = this.rememberedQuotaVariantCountry(treatmentCountry, residentialCountry);
    if (backendBucket) {
      return window.theme.pregnancyQuotaVariants.find(quota => quota.country.toLowerCase() === backendBucket)
        || globalPregnancyQuotaVariant;
    }

    const pregnancyQuotaVariant = window.theme.pregnancyQuotaVariants.find(quota => quota.country.toLowerCase() === treatmentCountry.toLowerCase());

    if (!pregnancyQuotaVariant || residentialCountry !== treatmentCountry) {
      return globalPregnancyQuotaVariant;
    }

    return pregnancyQuotaVariant;
  }

  // The flat-priced quota variant for scarce donors, or null when the donor's
  // worldwide family cap (donor.global_family_limit metafield) is unset or
  // above MAX_FIVE_FAMILY_LIMIT. Selected via the variant's
  // pregnancy_quota.country metafield value "max-5" — blanking that metafield
  // (or removing the variant) is the kill switch: selection falls back to the
  // country buckets.
  maxFiveQuotaVariant (donorFamilyLimit)
  {
    const limit = parseInt(donorFamilyLimit, 10);

    if (isNaN(limit) || limit > PregnancyQuotas.MAX_FIVE_FAMILY_LIMIT) {
      return null;
    }

    return window.theme.pregnancyQuotaVariants.find(quota => quota.country === 'max-5') || null;
  }

  // Remember the backend's resolved pricing bucket (e.g. 'gb', 'dk', 'global')
  // for a treatment+residence combo, captured from a /quota-available response.
  // Lets getPregnancyQuotaVariant() (product page AND cart) price a quota against
  // the bucket the backend actually counts it in, including cross-border cases.
  // Persisted so it survives the product→cart navigation; keyed by the combo so a
  // changed residence/country can't read a stale value. A missing value is a
  // no-op — until the backend returns the field, the heuristic is used unchanged.
  rememberQuotaVariantCountry (treatmentCountry, residentialCountry, variantCountry)
  {
    if (!treatmentCountry || !variantCountry) {
      return;
    }

    app.cache.forever(this.quotaVariantCacheKey(treatmentCountry, residentialCountry), ('' + variantCountry).toLowerCase());
  }

  rememberedQuotaVariantCountry (treatmentCountry, residentialCountry)
  {
    return app.cache.get(this.quotaVariantCacheKey(treatmentCountry, residentialCountry));
  }

  quotaVariantCacheKey (treatmentCountry, residentialCountry)
  {
    return 'pq_variant_bucket|' + ('' + (treatmentCountry || '')).toUpperCase() + '|' + ('' + (residentialCountry || '')).toUpperCase();
  }
}
